import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchAdminUserActivity } from "../features/admin/slice-extensions/admin-user-activity-slice-extension";
import {
  selectAdminUserActivity,
  selectAdminUserActivityStatus,
} from "../features/admin/admin-slice";
import { formatAdminTimestamp } from "../features/admin/utils/formatAdminTimestamp";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminActivationFunnel from "../components/admin/AdminActivationFunnel";
import AdminActivityTimeline from "../components/admin/AdminActivityTimeline";

/** One account's activation funnel and timeline — a read-only back-office view. */
const AdminUserActivityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const activity = useAppSelector(selectAdminUserActivity);
  const status = useAppSelector(selectAdminUserActivityStatus);

  useEffect(() => {
    if (id) {
      dispatch(fetchAdminUserActivity(id));
    }
  }, [dispatch, id]);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <Link
                to={ROUTES.ADMIN_USERS}
                className="flex items-center gap-1.5 text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
              >
                <ArrowLeft size={16} />
                All users
              </Link>

              {status === LoadingStatus.Failed && (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>{t("admin.errorLoading")}</p>
                </div>
              )}

              {status === LoadingStatus.Loading || !activity ? (
                status !== LoadingStatus.Failed && (
                  <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                    {t("admin.loading")}
                  </p>
                )
              ) : (
                <>
                  <div>
                    <h1 className="text-2xl font-semibold text-grey-10 dark:text-white">
                      {activity.organizationName}
                    </h1>
                    <p className="text-sm text-grey-40 dark:text-grey-70">
                      Joined {formatAdminTimestamp(activity.signupDate)} · {t("admin.activity.lastActive")}{" "}
                      {/* "No activity yet" = no records yet; lastActiveAt also moves at sign-up. */}
                      {activity.funnel.some((step) => step.count > 0) && activity.lastActiveAt
                        ? formatAdminTimestamp(activity.lastActiveAt)
                        : t("admin.activity.never")}
                    </p>
                  </div>
                  <AdminActivationFunnel steps={activity.funnel} />
                  <AdminActivityTimeline events={activity.timeline} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminUserActivityPage;
